import type { ApolloError } from "@apollo/client";
import type { InboxConversationQuery, InboxFullMessageFragment } from "@qw/graphql";
import type { MaterialSymbol } from "@qw/ui";
import { createContext, useContext } from "react";

interface ButtonConversationMenuItem {
  type: "button";
  label: string;
  icon: MaterialSymbol;
  onClick: () => void;
}

interface SeparatorConversationMenuItem {
  type: "separator";
}

export type ConversationMenuItem = ButtonConversationMenuItem | SeparatorConversationMenuItem;

export interface ConversationContextValue {
  conversationId?: string;
  displayFooters: boolean;
  displayHeaders: boolean;
  error?: ApolloError;
  inboxConversation: InboxConversationQuery["inboxConversation"] | null;
  loading: boolean;
  // Every message body in the thread, fetched in one batched query rather than one per rendered
  // message (B-1824), keyed by message id. A message the viewer may not read is simply absent —
  // `MessageDetail` renders the same "deleted or no access" state it already had for that case.
  messageBodiesById: Map<string, InboxFullMessageFragment>;
  messageBodiesLoading: boolean;
  messageBodiesError?: ApolloError;
  refetchQueries: string[];
  refetch: () => void;
  setDisplayFooters: React.Dispatch<React.SetStateAction<boolean>>;
  setDisplayHeaders: React.Dispatch<React.SetStateAction<boolean>>;
  // True when the conversation lives in the current user's own inbox.
  isOwnInbox: boolean;
  isReadOnly: boolean;
  // True when the user may reply but the thread lives in another user's inbox — i.e. they are
  // responding as a collaborator to a thread they were not originally on. Mutually exclusive with
  // `isReadOnly`. Together with `isOwnInbox` this is the predicate the server enforces for
  // chat-session creation, so the client gates `allowCreateChatSession` on the pair (see
  // `canCreateInboxChatSession`).
  isCollaboratorReply: boolean;
}

export const ConversationContext = createContext<ConversationContextValue | null>(null);

export const useConversation = () => {
  const context = useContext(ConversationContext);

  if (!context) {
    throw new Error("useConversation must be used within a ConversationProvider");
  }

  return context;
};
